import { createAsyncThunk, createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GetPaymentRequestResponse } from '@payer-admin-ui/api/src/paymentrequests/responses/GetPaymentRequestResponse';
import {
  FetchingEntityState,
  AppEntityState,
  SingleViewState,
  ListViewWithFilterAndSortState,
} from '@payer-admin-ui/components-v2/src/types/state';
import { SortDirection } from '@payer-admin-ui/components/src/Table/types';
import { chain, difference } from 'lodash';
import { availablePageSizes } from '../../../config';
import { MerchantAdminState } from '../../../redux/store';
import { paymentRequestAPI } from '../../../redux/paymentRequestAPI';
import { PaymentsSortableColumn } from '../payments/paymentsGlobalState';

export const fetchPaymentRequest = createAsyncThunk<GetPaymentRequestResponse, { id: string }>(
  'paymentrequests/fetchPaymentRequest',
  async ({ id }) => {
    return paymentRequestAPI.fetchById({ id });
  },
);

export type PaymentRequestFilters = Record<string, never>;
export type PaymentRequestEntity = FetchingEntityState<GetPaymentRequestResponse, string>;
export type PaymentRequestsGlobalState = AppEntityState<GetPaymentRequestResponse, string> &
  SingleViewState<string> &
  ListViewWithFilterAndSortState<string, PaymentRequestFilters, PaymentsSortableColumn>;

const entityAdapter = createEntityAdapter<PaymentRequestEntity>();
export const paymentRequestsSelectors = entityAdapter.getSelectors(
  (state: MerchantAdminState) => state.paymentRequests.global,
);

const pruneCache = (state: PaymentRequestsGlobalState) => {
  entityAdapter.removeMany(state, difference(state.ids, state.singleView.cachedIds, state.listView.ids));
};

const SINGLE_CACHE_SIZE = 10;

const upsertOnePruneCache = (state: PaymentRequestsGlobalState, entity: PaymentRequestEntity) => {
  entityAdapter.upsertOne(state, entity);
  state.singleView.cachedIds = chain([entity.id]).union(state.singleView.cachedIds).take(SINGLE_CACHE_SIZE).value();
  pruneCache(state);
};

const initialState: PaymentRequestsGlobalState = entityAdapter.getInitialState({
  singleView: {
    cachedIds: [],
  },
  listView: {
    isFetching: false,
    error: null,
    totalCount: null,
    ids: [],
    page: 0, // zero-indexed in state, one-indexed in UI
    pageSize: availablePageSizes[0],
    filters: {},
    appliedFilters: {},
    sortState: {
      column: PaymentsSortableColumn.externalPaymentReference,
      sortDirection: SortDirection.asc,
    },
  },
});

const paymentRequestSlice = createSlice({
  name: 'paymentrequests',
  initialState,
  reducers: {
    resetSingleError: (state, { payload: { id } }: PayloadAction<{ id: string }>) => {
      entityAdapter.upsertOne(state, {
        id,
        isFetching: false,
        success: undefined,
        error: null,
      });
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchPaymentRequest.pending, (state, { meta: { arg } }) => {
      upsertOnePruneCache(state, {
        id: arg.id,
        isFetching: true,
        success: undefined,
        error: null,
      });
    });
    builder.addCase(fetchPaymentRequest.fulfilled, (state, { meta: { arg }, payload: paymentRequest }) => {
      entityAdapter.upsertOne(state, {
        id: arg.id,
        entity: paymentRequest,
        isFetching: false,
        success: true,
        error: null,
      });
    });
    builder.addCase(fetchPaymentRequest.rejected, (state, { meta: { arg }, error }) => {
      entityAdapter.upsertOne(state, {
        id: arg.id,
        entity: undefined,
        isFetching: false,
        success: false,
        error,
      });
    });
  },
});

export const {
  actions: { resetSingleError },
  reducer: paymentRequestReducer,
} = paymentRequestSlice;
